import { useState } from 'react'

type CascadeOption = {
  label?: string
  value?: string | number
  children?: CascadeOption[]
  [key: string]: any
}

type FieldNames = {
  value?: string
  label?: string
  children?: string
}

type Props = {
  options?: CascadeOption[]
  onChange?: (
    values: (string | number)[],
    selectedOptions: CascadeOption[]
  ) => void
  value?: (string | number)[] | any
  defaultValue?: (string | number)[] | any
  fieldNames?: FieldNames
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  name?: string
  className?: string
  'data-testid'?: string
  // accepted for API compatibility, not used
  showSearch?: any
  getPopupContainer?: any
}

const getVal = (opt: CascadeOption, fn?: FieldNames) =>
  opt[fn?.value ?? 'value']

const getLabel = (opt: CascadeOption, fn?: FieldNames) =>
  opt[fn?.label ?? 'label'] ?? ''

const getChildren = (
  opt: CascadeOption,
  fn?: FieldNames
): CascadeOption[] | undefined => opt[fn?.children ?? 'children']

const findOpt = (
  opts: CascadeOption[],
  val: string | number,
  fn?: FieldNames
) => opts.find((o) => String(getVal(o, fn)) === String(val))

const toPathArray = (val: any): (string | number)[] => {
  if (!val) {
    return []
  }
  if (Array.isArray(val)) {
    return val
  }
  return [val]
}

const CascadeSelect = ({
  options = [],
  onChange,
  value,
  defaultValue,
  fieldNames,
  placeholder = 'Please select',
  disabled = false,
  className,
  'data-testid': testId,
}: Props) => {
  const resolvePathFromValues = (
    vals: (string | number)[]
  ): CascadeOption[] => {
    const path: CascadeOption[] = []
    let current = options
    for (const val of vals) {
      const found = findOpt(current, val, fieldNames)
      if (!found) {
        break
      }
      path.push(found)
      const children = getChildren(found, fieldNames)
      if (!children?.length) {
        break
      }
      current = children
    }
    return path
  }

  const initialVals = toPathArray(value ?? defaultValue)
  const [selectedPath, setSelectedPath] = useState<CascadeOption[]>(() =>
    resolvePathFromValues(initialVals)
  )

  // sync controlled value
  const controlledPath =
    value !== undefined ? resolvePathFromValues(toPathArray(value)) : null
  const activePath = controlledPath ?? selectedPath

  const handleSelect = (opt: CascadeOption, levelIndex: number) => {
    const newPath = [...activePath.slice(0, levelIndex), opt]
    setSelectedPath(newPath)
    const children = getChildren(opt, fieldNames)
    if (!children?.length) {
      onChange?.(
        newPath.map((o) => getVal(o, fieldNames)),
        newPath
      )
    }
  }

  // build list of levels to show
  const levels: CascadeOption[][] = [options]
  for (const selected of activePath) {
    const children = getChildren(selected, fieldNames)
    if (children?.length) {
      levels.push(children)
    } else {
      break
    }
  }

  return (
    <div className={className} data-testid={testId}>
      {levels.map((levelOptions, i) => {
        const selected = activePath[i]
        const selectedVal =
          selected !== undefined ? String(getVal(selected, fieldNames)) : ''

        return (
          <div key={i} className="mb-1">
            <select
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-maroon-400"
              value={selectedVal}
              disabled={disabled}
              onChange={(e) => {
                const found = findOpt(levelOptions, e.target.value, fieldNames)
                if (found) {
                  handleSelect(found, i)
                }
              }}
            >
              <option value="">{i === 0 ? placeholder : 'Select...'}</option>
              {levelOptions.map((opt) => {
                const v = String(getVal(opt, fieldNames))
                return (
                  <option key={v} value={v}>
                    {getLabel(opt, fieldNames)}
                  </option>
                )
              })}
            </select>
          </div>
        )
      })}
    </div>
  )
}

export default CascadeSelect
